import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { IChatBotResponse } from '../interfaces/chatbot/response/IChatBotResponse';
import { IChatConversationListResponse } from '../interfaces/chatbot/response/IChatConversationListResponse';
import { IChatHistoryResponse } from '../interfaces/chatbot/response/IChatHistoryResponse';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { ExplorerMapService } from './explorer-map.service';
import { TChatBotCommand } from '../types/explorer/chatbot/TChatBotCommand';
import { IChatBotAreaCommand } from '../interfaces/chatbot/response/command/IChatBotAreaCommand';
import { IChatBotZoomCommand } from '../interfaces/chatbot/response/command/IChatBotZoomCommand';
import { IChatBotMarkerCommand } from '../interfaces/chatbot/response/command/IChatBotMarkerCommand';
import maplibregl, { AddLayerObject } from 'maplibre-gl';
import { bbox as turfBbox } from '@turf/turf';

@Injectable({
  providedIn: 'root',
})
export class ChatbotService {
  private http: HttpClient = inject(HttpClient);
  private explorerMapService: ExplorerMapService = inject(ExplorerMapService);
  public sidebarOpenSubject = new BehaviorSubject<boolean>(true);
  public sidebarWidth$ = new BehaviorSubject<number>(400);
  sidebarOpen$ = this.sidebarOpenSubject.asObservable();
  public zoomOptions$ = new BehaviorSubject<
    Array<{ name: string; bounds: [number, number, number, number] }>
  >([]);
  private lastCommandsSignature: string = '';
  private readonly renderedAreaLayers = new Map<string, string>();

  openSidebar() {
    this.sidebarOpenSubject.next(true);
  }

  expandSidebar(value: number) {
    this.sidebarWidth$.next(value);
  }

  private readonly apiUrl = `${environment.services.urlEDI}/chatbot`;

  sendPrompt(
    prompt: string,
    projectUuid: string,
    chatUuid: string | null,
  ): Observable<IChatBotResponse> {
    return this.http.post<IChatBotResponse>(this.apiUrl, {
      prompt,
      project_uuid: projectUuid,
      chat_uuid: chatUuid,
    });
  }

  getConversations(
    projectUuid: string,
    limit = 20,
    cursor: string | null = null,
    archived = false,
  ): Observable<IChatConversationListResponse> {
    let params = new HttpParams()
      .set('project_uuid', projectUuid)
      .set('limit', limit)
      .set('archived', archived);

    if (cursor) {
      params = params.set('cursor', cursor);
    }

    return this.http.get<IChatConversationListResponse>(`${this.apiUrl}/chats`, {
      params,
    });
  }

  getConversationMessages(
    projectUuid: string,
    chatUuid: string,
  ): Observable<IChatHistoryResponse> {
    const params = new HttpParams().set('project_uuid', projectUuid);

    return this.http.get<IChatHistoryResponse>(
      `${this.apiUrl}/chats/${chatUuid}/messages`,
      { params },
    );
  }

  setChatArchived(
    projectUuid: string,
    chatUuid: string,
    archived: boolean,
  ): Observable<void> {
    const params = new HttpParams().set('project_uuid', projectUuid);
    return this.http.patch<void>(
      `${this.apiUrl}/chats/${chatUuid}`,
      { archived },
      { params },
    );
  }

  deleteConversation(projectUuid: string, chatUuid: string): Observable<void> {
    const params = new HttpParams().set('project_uuid', projectUuid);
    return this.http.delete<void>(`${this.apiUrl}/chats/${chatUuid}`, { params });
  }

  deleteLayer(
    projectUuid: string,
    chatUuid: string,
    layerUuid: string,
  ): Observable<void> {
    const params = new HttpParams().set('project_uuid', projectUuid);

    return this.http.delete<void>(
      `${this.apiUrl}/chats/${chatUuid}/layers/${layerUuid}`,
      { params },
    );
  }

  resetCommandState(): void {
    this.clearAreaCommands();
    this.lastCommandsSignature = '';
    this.zoomOptions$.next([]);
  }

  private clearAreaCommands(): void {
    const map = this.explorerMapService.getMap();
    if (map) {
      for (const [layerId, sourceId] of this.renderedAreaLayers) {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      }
    }
    this.renderedAreaLayers.clear();
  }

  applyCommands(commands: TChatBotCommand[] | undefined | null): void {
    if (!commands || !Array.isArray(commands)) {
      this.zoomOptions$.next([]);
      return;
    }
    // Prevent re-applying the exact same command set (avoids loops/duplications)
    const signature = safeStableStringify(commands);
    if (signature && signature === this.lastCommandsSignature) {
      return;
    }
    this.lastCommandsSignature = signature;
    const zoomOptions: Array<{
      name: string;
      bounds: [number, number, number, number];
    }> = [];
    for (const cmd of commands) {
      if (!cmd || !cmd.type) continue;
      if (cmd.type === 'area') {
        this.handleAreaCommand(cmd as IChatBotAreaCommand);
      } else if (cmd.type === 'zoom') {
        const z = cmd as IChatBotZoomCommand;
        const minLon = Number(z.bounds.southwest.lng);
        const minLat = Number(z.bounds.southwest.lat);
        const maxLon = Number(z.bounds.northeast.lng);
        const maxLat = Number(z.bounds.northeast.lat);
        zoomOptions.push({
          name: cmd.name,
          bounds: [minLon, minLat, maxLon, maxLat],
        });
      } else if (cmd.type === 'marker') {
        this.handleMarkerCommand(cmd as IChatBotMarkerCommand);
      } else if (cmd.type === 'raster') {
        // Persistent raster layers are rendered from response.generated_layers
        // by ChatbotLayerMapService. Raster commands are kept only for API
        // backward compatibility and must not create a second MapLibre copy.
        continue;
      }
    }
    this.zoomOptions$.next(zoomOptions);
  }

  zoomTo(bounds: [number, number, number, number]): void {
    this.explorerMapService.zoomToBoundingBox(bounds);
  }

  private handleAreaCommand(cmd: IChatBotAreaCommand): void {
    const map = this.explorerMapService.getMap();
    if (
      !map ||
      cmd.geometry?.type !== 'FeatureCollection' ||
      !Array.isArray(cmd.geometry.features) ||
      cmd.geometry.features.length === 0 ||
      !cmd.style.id
    ) {
      return;
    }

    const sourceId = this.buildSourceId(cmd.name);
    const existing = map.getSource(sourceId);
    if (existing) {
      (existing as maplibregl.GeoJSONSource).setData(cmd.geometry);
    } else {
      this.explorerMapService.addSource(sourceId, {
        type: 'geojson',
        data: cmd.geometry,
      });
    }

    if (!map.getLayer(cmd.style.id)) {
      const style = { ...cmd.style, source: sourceId } as AddLayerObject;
      this.explorerMapService.addLayer(style);
    }
    this.renderedAreaLayers.set(cmd.style.id, sourceId);
    
    if (!cmd.style.id.startsWith('tala-districts-')) return;
    const bounds = turfBbox(cmd.geometry);
    if (
      bounds.length >= 4 &&
      bounds.slice(0, 4).every(Number.isFinite) &&
      bounds[0] < bounds[2] &&
      bounds[1] < bounds[3]
    ) {
      this.explorerMapService.zoomToBoundingBox([
        bounds[0], bounds[1], bounds[2], bounds[3],
      ]);
    }
  }

  private buildSourceId(name: string): string {
    const base = (name || 'chatbot-area')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-');
    return `chatbot-src-${base}`;
  }

  private handleMarkerCommand(cmd: IChatBotMarkerCommand): void {
    const map = this.explorerMapService.getMap();
    if (!map) return;
    for (const category of cmd.categories || []) {
      const color = category?.style?.color || '#0A6FFD';
      for (const p of category.points || []) {
        const lng = Number(p.coordinates?.lng);
        const lat = Number(p.coordinates?.lat);
        if (isNaN(lng) || isNaN(lat)) continue;
        const el = document.createElement('div');
        el.style.width = '12px';
        el.style.height = '12px';
        el.style.borderRadius = '50%';
        el.style.background = color;
        el.style.boxShadow = '0 0 0 2px #ffffff, 0 0 6px rgba(0,0,0,0.4)';
        const popupContent = this.buildPayloadPopup(
          cmd.name,
          category.name,
          p.payload,
        );
        const marker = new maplibregl.Marker({ element: el }).setLngLat([lng, lat]);
        if (popupContent) {
          marker.setPopup(
            new maplibregl.Popup({ closeButton: true, maxWidth: '360px' })
              .setDOMContent(popupContent),
          );
        }
        marker.addTo(map);
      }
    }
  }

  private buildPayloadPopup(
    cmdName: string,
    categoryName: string,
    payload: Record<string, unknown>,
  ): HTMLElement | null {
    try {
      const container = document.createElement('div');
      container.style.cssText =
        'font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;font-size:12px;';

      const commandTitle = document.createElement('div');
      commandTitle.style.cssText =
        'font-weight:700;margin-bottom:6px;color:#0A6FFD;';
      commandTitle.textContent = cmdName;
      container.appendChild(commandTitle);

      const categoryTitle = document.createElement('div');
      categoryTitle.style.cssText =
        'font-weight:600;margin-bottom:8px;color:#333;';
      categoryTitle.textContent = categoryName;
      container.appendChild(categoryTitle);

      const tableContainer = document.createElement('div');
      tableContainer.style.cssText =
        'background:#fff;border-radius:8px;box-shadow:0 2px 10px rgba(0,0,0,0.08);overflow:hidden;border:1px solid #eee;';

      const table = document.createElement('table');
      table.style.cssText = 'border-collapse:collapse;width:100%;';

      for (const [key, value] of Object.entries(payload || {})) {
        const row = document.createElement('tr');

        const header = document.createElement('th');
        header.style.cssText =
          'text-align:left;padding:6px 10px;color:#0A6FFD;white-space:nowrap;';
        header.textContent = key;

        const cell = document.createElement('td');
        cell.style.cssText = 'padding:6px 10px;color:#111;';
        cell.textContent = this.stringifyValue(value);

        row.append(header, cell);
        table.appendChild(row);
      }

      tableContainer.appendChild(table);
      container.appendChild(tableContainer);
      return container;
    } catch {
      return null;
    }
  }

  private stringifyValue(v: unknown): string {
    if (Array.isArray(v)) return v.join(', ');
    if (v && typeof v === 'object') return JSON.stringify(v);
    return String(v ?? '');
  }
}

// Util per stabilizzare JSON stringify ed evitare false diffs
function safeStableStringify(value: unknown): string {
  try {
    return JSON.stringify(value, replacer);
  } catch {
    return '';
  }
  function replacer(_key: string, val: unknown) {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const obj = val as Record<string, unknown>;
      // Ordina le chiavi per stabilità
      return Object.keys(obj)
        .sort()
        .reduce((acc: Record<string, unknown>, k: string) => {
          acc[k] = obj[k];
          return acc;
        }, {} as Record<string, unknown>);
    }
    return val;
  }
}
